import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Link as MuiLink, Divider } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../utils/axiosConfig';
import { dateLocale } from '../utils/dateLocale';

const PinnedForums = () => {
    const { t, i18n } = useTranslation();
    const [pinnedForums, setPinnedForums] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        fetchPinnedForums();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Refetch when language changes
    useEffect(() => {
        const handleLanguageChange = () => {
            fetchPinnedForums();
        };

        i18n.on('languageChanged', handleLanguageChange);

        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n]);

    const fetchPinnedForums = async () => {
        try {
            const response = await axios.get('/api/forums/pinned', {
                params: { language: i18n.language }
            });
            setPinnedForums(response.data);
        } catch (err) {
            console.error('Error fetching pinned forums:', err);
        }
    };

    if (pinnedForums.length === 0) return null;

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(dateLocale());
    };

    // Helper to get page for thread
    const getThreadPage = async (forumId, threadId, perPage = 5) => {
        const res = await axios.get(`/api/threads/forum/${forumId}`, { params: { limit: 10000, page: 1 } });
        const threads = res.data.threads;
        const idx = threads.findIndex(t => t.id === threadId || t.slug === threadId);
        return idx !== -1 ? Math.floor(idx / perPage) + 1 : 1;
    };

    const handleThreadClick = async (forum, thread) => {
        const forumSlug = forum.slug || forum.id;
        const threadSlug = thread.slug || thread.id;
        const forumId = forum.id;
        const threadId = thread.id || thread.slug;
        const page = await getThreadPage(forumId, threadId, 5);
        navigate(`/dashboard/forums/${forumSlug}/threads/${threadSlug}?page=${page}`);
    };

    return (
        <Box sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom>
                {t('pinnedForums.title')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {pinnedForums.map((forum) => (
                    <Paper key={forum.id} elevation={2} sx={{ flex: '1 1 300px', p: 2 }}>
                        <Typography
                            variant="h6"
                            component={Link}
                            to={`/dashboard/forums/${forum.slug || forum.id}`}
                            sx={{
                                textDecoration: 'none',
                                color: 'primary.main',
                                '&:hover': { textDecoration: 'underline' }
                            }}
                        >
                            {i18n.language === 'fr' ? forum.frenchName : forum.englishName}
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        {forum.threads && forum.threads.length > 0 ? (
                            <Box>
                                {forum.threads.map((thread) => (
                                    <Box key={thread.id} sx={{ mb: 1.5 }}>
                                        <Typography
                                            variant="body2"
                                            component="span"
                                            sx={{
                                                textDecoration: 'none',
                                                color: 'text.primary',
                                                fontWeight: 500,
                                                display: 'block',
                                                cursor: 'pointer',
                                                '&:hover': { color: 'primary.main' }
                                            }}
                                            onClick={() => handleThreadClick(forum, thread)}
                                        >
                                            {thread.title}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('pinnedForums.by')}: {thread.author?.firstName} {thread.author?.lastName} | {formatDate(thread.createdAt)}
                                        </Typography>
                                    </Box>
                                ))}
                                <MuiLink
                                    component={Link}
                                    to={`/dashboard/forums/${forum.slug || forum.id}`}
                                    variant="caption"
                                    sx={{ mt: 1, display: 'block', textAlign: 'right' }}
                                >
                                    {t('pinnedForums.viewMore')} →
                                </MuiLink>
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {t('pinnedForums.noThreads')}
                            </Typography>
                        )}
                    </Paper>
                ))}
            </Box>
        </Box>
    );
};

export default PinnedForums;
